import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { ShareIcon, AddIcon } from "./icons";
import {
  Backdrop,
  Sheet,
  Handle,
  SheetTitle,
  Body,
  Steps,
  Step,
  StepNumber,
  StepText,
  StepTitle,
  StepHint,
  StepIcon,
  StepLogo,
  Note,
  SheetButton,
} from "./styled";

const LOGO = `${process.env.PUBLIC_URL}/logo192.png`;

// Each iPhone browser hides Share in a different place; the other steps are the same everywhere.
const FIRST_STEP = {
  safari: {
    title: "Tap ⋯ at the bottom, then Share",
    hint: "On older iPhones, Share is at the bottom of Safari",
    fallback: "Don't see Share or “Add to Home Screen”? Open this page in Safari first.",
  },
  chrome: {
    title: "Tap the Share icon",
    hint: "At the right of Chrome's address bar, at the top",
    fallback: "Don't see “Add to Home Screen”? Open this page in Safari instead.",
  },
  other: {
    title: "Open the menu, then Share",
    hint: "Firefox: ☰ at the bottom right. Edge: ⋯ at the bottom. Other browsers: their menu.",
    fallback: "Don't see Share or “Add to Home Screen”? Open this page in Safari instead.",
  },
};

// Apple doesn't let a website open an install dialog, so "Install app" opens these steps instead.
const IOSInstallGuide = ({ browser, onClose }) => {
  const sheet = useRef(null);
  const button = useRef(null);
  const step = FIRST_STEP[browser] || FIRST_STEP.safari;

  useEffect(() => {
    sheet.current.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      // "Got it" is the only control, so Tab stays on it.
      if (event.key === "Tab") {
        event.preventDefault();
        button.current.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <Backdrop onClick={(event) => event.target === event.currentTarget && onClose()}>
      <Sheet ref={sheet} role="dialog" aria-modal="true" aria-labelledby="install-guide-title" tabIndex={-1}>
        <Handle aria-hidden="true" />
        <div>
          <SheetTitle id="install-guide-title">Add Nutivate Coach to your Home Screen</SheetTitle>
          <Body>It takes 10 seconds. Then you'll get a notification whenever a client messages you.</Body>
        </div>

        <Steps>
          <Step>
            <StepNumber aria-hidden="true">1</StepNumber>
            <StepText>
              <StepTitle>{step.title}</StepTitle>
              <StepHint>{step.hint}</StepHint>
            </StepText>
            <StepIcon color="#0a7aff">
              <ShareIcon />
            </StepIcon>
          </Step>
          <Step>
            <StepNumber aria-hidden="true">2</StepNumber>
            <StepText>
              <StepTitle>Tap “Add to Home Screen”</StepTitle>
              <StepHint>Scroll down the list if you don't see it</StepHint>
            </StepText>
            <StepIcon>
              <AddIcon />
            </StepIcon>
          </Step>
          <Step>
            <StepNumber aria-hidden="true">3</StepNumber>
            <StepText>
              <StepTitle>Tap “Add”, open Nutivate Coach, sign in</StepTitle>
              <StepHint>Leave “Open as Web App” on. It opens on your conversations.</StepHint>
            </StepText>
            <StepLogo src={LOGO} alt="" />
          </Step>
        </Steps>

        <Note style={{ textAlign: "center" }}>{step.fallback}</Note>
        <SheetButton ref={button} type="button" onClick={onClose}>
          Got it
        </SheetButton>
      </Sheet>
    </Backdrop>,
    document.body,
  );
};

export default IOSInstallGuide;
